import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { numericFormatter } from 'react-number-format';

import { Grid, Typography } from '@material-ui/core';
import { Row } from '../../Resources';
import ActivitiesRegistrationHeader from 'components/ActivitiesRegistrationDetails/ActivitiesRegistrationHeader';

import { loadProductsFilters } from 'pages/Products/store/thunk';

const hasKey = (register, key) => Object.keys(register || {}).includes(key);

const formatDecimal = value => {
  if(value === null || value === undefined || value === '') {
    return '--';
  }

  return numericFormatter(value?.toString(), {
    thousandSeparator: '.',
    decimalSeparator: ',',
    decimalScale: 2,
    fixedDecimalScale: true
  });
}

function RegisterFields({
  register,
  areasOfUse,
  productBrands,
  productTypes,
  measurementUnits,
  services
}) {
  const getAreasOfUseNames = areaIds => {
    let ids = areaIds;

    if(typeof ids === 'string') {
      try {
        ids = JSON.parse(ids);
      } catch(e) {
        ids = [];
      }
    }

    if(!Array.isArray(ids) || !ids.length) {
      return '--';
    }

    return areasOfUse
      ?.filter(area => ids.map(Number).includes(+area?.id))
      ?.map(area => area?.description)
      ?.join(', ') || '--';
  }

  const getProductBrandName = productBrandId => {
    return productBrands?.find(brand => +brand?.id == +productBrandId)?.name || '--';
  }

  const getProductTypeName = productTypeId => {
    return productTypes?.find(type => +type?.id == +productTypeId)?.description || '--';
  }

  const getMeasurementUnitName = measurementUnitId => {
    const unit = measurementUnits?.find(item => +item?.id == +measurementUnitId);

    if(!unit) {
      return '--';
    }

    return unit?.abbreviation
      ? `${unit?.description} (${unit?.abbreviation})`
      : unit?.description || '--';
  }

  const getServiceName = serviceId => {
    return services?.find(service => +service?.serviceId == +serviceId)?.description || '--';
  }

  return(
    <>
      {hasKey(register, 'name') && (
        <Row>
          <Typography color='primary'>
            <b>Nome</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {register?.name || '--'}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'visible') && (
        <Row>
          <Typography color='primary'>
            <b>Status</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {+register?.visible == 1 ? 'Ativado' : 'Desativado'}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'areasOfUse') && (
        <Row>
          <Typography color='primary'>
            <b>Áreas de uso</b>
          </Typography>
          <Typography color='textSecondary' style={{ marginLeft: 5 }}>
            {getAreasOfUseNames(register?.areasOfUse)}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'netContent') && (
        <Row>
          <Typography color='primary'>
            <b>Quantidade de unidade de medida por item</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {formatDecimal(register?.netContent)}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'productTypeId') && (
        <Row>
          <Typography color='primary'>
            <b>Categoria</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {getProductTypeName(register?.productTypeId)}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'productBrandId') && (
        <Row>
          <Typography color='primary'>
            <b>Marca</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {getProductBrandName(register?.productBrandId)}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'minQuantityToUse') && (
        <Row>
          <Typography color='primary'>
            <b>Quantidade mínima para uso</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {formatDecimal(register?.minQuantityToUse)}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'measurementUnitId') && (
        <Row>
          <Typography color='primary'>
            <b>Unidade de medida</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {getMeasurementUnitName(register?.measurementUnitId)}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'measurementUnitName') && (
        <Row>
          <Typography color='primary'>
            <b>Nome da unidade de medida</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {register?.measurementUnitName || '--'}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'serviceId') && (
        <Row>
          <Typography color='primary'>
            <b>Serviço</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {getServiceName(register?.serviceId)}
          </Typography>
        </Row>
      )}
      {hasKey(register, 'toBeUsedContent') && (
        <Row>
          <Typography color='primary'>
            <b>Consumo por serviço</b>
          </Typography>
          <Typography color='textSecondary' style={{ whiteSpace: 'nowrap', marginLeft: 5 }}>
            {formatDecimal(register?.toBeUsedContent)}
          </Typography>
        </Row>
      )}
    </>
  );
}

export default function ProductConsumeUpdate({ activityData, lastRegister, newRegister }) {
  const dispatch = useDispatch();

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { selects } = useSelector(state => state.filters);
  const {
    areasOfUse,
    productBrands,
    productTypes,
    measurementUnits
  } = useSelector(state => state.products.filters);

  const services = selects?.find(select => select?.id == 'services')?.options;

  useEffect(() => {
    if(userId && establishmentId) {
      dispatch(loadProductsFilters(userId, establishmentId));
    }
  }, [userId, establishmentId]);

  return(
    <Grid container>
      <Grid xs={12}>
        <ActivitiesRegistrationHeader
          activityData={activityData}
        />
        <Grid container style={{ marginTop: 10 }}>
          <Grid xs={12} style={{ padding: 5 }}>
            <Typography color='primary' style={{ marginBottom: 5, borderBottom: '1px solid #E4E7EA' }}>
              <b>Registro Anterior</b>
            </Typography>
            <Grid style={{ border: '1px solid #D7D7D7', borderRadius: 5, backgroundColor: '#FCFCFC', padding: 8, marginTop: 10 }}>
              <RegisterFields
                register={lastRegister}
                areasOfUse={areasOfUse}
                productBrands={productBrands}
                productTypes={productTypes}
                measurementUnits={measurementUnits}
                services={services}
              />
            </Grid>
          </Grid>
          <Grid xs={12} style={{ padding: 5 }}>
            <Typography color='primary' style={{ marginTop: 10, marginBottom: 5, borderBottom: '1px solid #E4E7EA' }}>
              <b>Novo Registro</b>
            </Typography>
            <Grid style={{ border: '1px solid #D7D7D7', borderRadius: 5, backgroundColor: '#FCFCFC', padding: 8, marginTop: 10 }}>
              <RegisterFields
                register={newRegister}
                areasOfUse={areasOfUse}
                productBrands={productBrands}
                productTypes={productTypes}
                measurementUnits={measurementUnits}
                services={services}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}